/**
 * Leaderboard Page - Season, Annual, and All-Time rankings
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { FinancialTooltip } from '../components/FinancialTooltip';
import { 
  Trophy, Medal, Crown, Users, Target, Calendar,
  Infinity, TrendingUp, TrendingDown, ChevronDown, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LeaderboardPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('season');
  const [loading, setLoading] = useState(true);
  const [seasonData, setSeasonData] = useState({ leaderboard: [], stats: {}, myRanking: null });
  const [annualData, setAnnualData] = useState({ leaderboard: [], stats: {}, year: null, availableYears: [] });
  const [allTimeData, setAllTimeData] = useState({ leaderboard: [], stats: {} });
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [gameInfo, setGameInfo] = useState(null);

  // Fetch game info
  useEffect(() => {
    const fetchGameInfo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/game-info`);
        if (res.ok) setGameInfo(await res.json());
      } catch (err) {
        console.error('Error fetching game info:', err);
      }
    };
    fetchGameInfo();
  }, []);

  // Fetch season leaderboard (default)
  const fetchSeasonData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [lbRes, statsRes, rankRes] = await Promise.all([
        fetch(`${API_URL}/api/leaderboard/global?limit=50`, { headers }),
        fetch(`${API_URL}/api/leaderboard/stats`),
        token ? fetch(`${API_URL}/api/leaderboard/me`, { headers }) : Promise.resolve(null)
      ]);

      const leaderboard = lbRes.ok ? await lbRes.json() : [];
      const stats = statsRes.ok ? await statsRes.json() : {};
      const myRanking = rankRes?.ok ? await rankRes.json() : null;

      setSeasonData({ leaderboard, stats, myRanking });
    } catch (err) {
      console.error('Error fetching season data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch annual leaderboard
  const fetchAnnualData = useCallback(async (year) => {
    setLoading(true);
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [lbRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/leaderboard/annual?year=${year}&limit=50`, { headers }),
        fetch(`${API_URL}/api/leaderboard/annual/stats?year=${year}`)
      ]);

      const lbData = lbRes.ok ? await lbRes.json() : { leaderboard: [], available_years: [] };
      const stats = statsRes.ok ? await statsRes.json() : {};

      setAnnualData({
        leaderboard: lbData.leaderboard || [],
        stats,
        year: lbData.year,
        availableYears: lbData.available_years || [new Date().getFullYear()]
      });
      setSelectedYear(lbData.year || new Date().getFullYear());
    } catch (err) {
      console.error('Error fetching annual data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch all-time leaderboard
  const fetchAllTimeData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [lbRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/leaderboard/all-time?limit=50`, { headers }),
        fetch(`${API_URL}/api/leaderboard/all-time/stats`)
      ]);

      const lbData = lbRes.ok ? await lbRes.json() : { leaderboard: [] };
      const stats = statsRes.ok ? await statsRes.json() : {};

      setAllTimeData({
        leaderboard: lbData.leaderboard || [],
        stats
      });
    } catch (err) {
      console.error('Error fetching all-time data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === 'season') fetchSeasonData();
    else if (activeTab === 'annual') fetchAnnualData(selectedYear);
    else if (activeTab === 'all-time') fetchAllTimeData();
  }, [activeTab, fetchSeasonData, fetchAnnualData, fetchAllTimeData, selectedYear]);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="h-5 w-5 text-yellow-400" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-slate-300" />;
    if (rank === 3) return <Medal className="h-5 w-5 text-amber-600" />;
    return <span className="text-slate-400 font-mono">#{rank}</span>;
  };

  const getRankBg = (rank, isCurrentUser) => {
    if (isCurrentUser) return 'bg-emerald-500/20 border-emerald-500/50';
    if (rank === 1) return 'bg-yellow-500/10 border-yellow-500/30';
    if (rank === 2) return 'bg-slate-400/10 border-slate-400/30';
    if (rank === 3) return 'bg-amber-500/10 border-amber-500/30';
    return 'bg-slate-800/50 border-slate-700/50';
  };

  const tabs = [
    { id: 'season', label: 'Season', icon: Trophy, description: 'Current quarter rankings' },
    { id: 'annual', label: 'Annual', icon: Calendar, description: 'Calendar year standings' },
    { id: 'all-time', label: 'All-Time', icon: Infinity, description: 'Lifetime achievements' }
  ];

  const currentStats = activeTab === 'season' ? seasonData.stats 
    : activeTab === 'annual' ? annualData.stats 
    : allTimeData.stats;

  const currentLeaderboard = activeTab === 'season' ? seasonData.leaderboard 
    : activeTab === 'annual' ? annualData.leaderboard 
    : allTimeData.leaderboard;

  return (
    <div className="min-h-screen bg-slate-950 pb-20" data-testid="leaderboard-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-slate-900 to-slate-950 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
                <Trophy className="h-8 w-8 text-amber-500" />
                Leaderboards
              </h1>
              <p className="text-slate-400 mt-1">
                {gameInfo ? `Season ${gameInfo.current_season} - Week ${gameInfo.current_week}` : 'Loading...'}
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-6 overflow-x-auto pb-2">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <Button
                  key={tab.id}
                  variant={activeTab === tab.id ? 'default' : 'outline'}
                  onClick={() => setActiveTab(tab.id)}
                  className={activeTab === tab.id 
                    ? 'bg-amber-600 hover:bg-amber-500' 
                    : 'border-slate-700 hover:bg-slate-800'
                  }
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.label}
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Description */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <p className="text-slate-400">
              {tabs.find(t => t.id === activeTab)?.description}
            </p>
          </div>
          
          {/* Year Selector for Annual */}
          {activeTab === 'annual' && annualData.availableYears.length > 0 && (
            <div className="relative">
              <select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value));
                  fetchAnnualData(parseInt(e.target.value));
                }}
                className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white appearance-none pr-10 cursor-pointer"
                data-testid="year-selector"
              >
                {annualData.availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Users className="h-8 w-8 text-indigo-500/50" />
                <div>
                  <p className="text-slate-400 text-sm">Total Players</p>
                  <p className="text-2xl font-bold text-white" data-testid="total-players">
                    {currentStats?.total_players || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Trophy className="h-8 w-8 text-amber-500/50" />
                <div>
                  <p className="text-slate-400 text-sm">Top Score</p>
                  <p className="text-2xl font-bold text-white" data-testid="top-score">
                    {currentStats?.top_score || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Target className="h-8 w-8 text-emerald-500/50" />
                <div>
                  <p className="text-slate-400 text-sm">Average Score</p>
                  <p className="text-2xl font-bold text-white" data-testid="avg-score">
                    {currentStats?.average_score || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <span className="text-emerald-400 font-bold text-sm">
                    {seasonData.myRanking?.rank ? `#${seasonData.myRanking.rank}` : '--'}
                  </span>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Your Rank</p>
                  <p className="text-2xl font-bold text-white" data-testid="your-rank">
                    {seasonData.myRanking?.total_points || 0} pts
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Your Position (Season only) */}
        {activeTab === 'season' && seasonData.myRanking && seasonData.myRanking.rank && (
          <Card className="bg-emerald-500/10 border-emerald-500/30 mb-8">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <span className="text-2xl font-bold text-emerald-400">#{seasonData.myRanking.rank}</span>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-lg">{seasonData.myRanking.username}</p>
                    <p className="text-emerald-400">Your Position</p>
                  </div>
                </div>
                <div className="text-center md:text-right">
                  <p className="text-3xl font-bold text-white">{seasonData.myRanking.total_points} pts</p>
                  <p className="text-slate-400">Season Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
          </div>
        )}

        {/* Leaderboard Table */}
        {!loading && (
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                {activeTab === 'season' && 'Season Rankings'}
                {activeTab === 'annual' && `${selectedYear} Annual Rankings`}
                {activeTab === 'all-time' && 'All-Time Legends'}
                {activeTab === 'all-time' && (
                  <Badge className="bg-purple-500/20 text-purple-400">Hall of Fame</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {currentLeaderboard.length > 0 ? (
                <div className="space-y-2">
                  {currentLeaderboard.map((entry) => (
                    <div
                      key={entry.user_id}
                      onClick={() => !entry.is_bot && navigate(`/player/${entry.user_id}`)}
                      className={`flex items-center justify-between p-4 rounded-lg border ${getRankBg(entry.rank, entry.is_current_user)} ${!entry.is_bot ? 'cursor-pointer hover:brightness-110 transition-all' : ''}`}
                      data-testid={`leaderboard-entry-${entry.rank}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 flex items-center justify-center">
                          {getRankIcon(entry.rank)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold ${entry.is_current_user ? 'text-emerald-400' : 'text-white'}`}>
                              {entry.username}
                            </span>
                            {entry.is_current_user && (
                              <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">You</Badge>
                            )}
                            {activeTab === 'all-time' && entry.rank <= 10 && (
                              <Badge className="bg-purple-500/20 text-purple-400 text-xs">Legend</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-400">
                            {activeTab === 'season' && entry.portfolio_value && (
                              <span>V${entry.portfolio_value?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                            )}
                            {activeTab === 'all-time' && entry.member_since && (
                              <span>Member since {new Date(entry.member_since).getFullYear()}</span>
                            )}
                            {(activeTab === 'annual' || activeTab === 'all-time') && entry.weeks_played && (
                              <span>{entry.weeks_played} weeks played</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-white">
                          {entry.total_points?.toLocaleString() || 0}
                        </p>
                        <FinancialTooltip termKey="season_total">
                          <p className="text-slate-400 text-sm cursor-help underline decoration-dotted decoration-slate-600">total pts</p>
                        </FinancialTooltip>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Trophy className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">
                    {activeTab === 'season' && 'No rankings yet this season'}
                    {activeTab === 'annual' && `No rankings for ${selectedYear} yet`}
                    {activeTab === 'all-time' && 'No all-time rankings yet'}
                  </p>
                  <p className="text-slate-500 text-sm mt-1">Build your portfolio and start competing!</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default LeaderboardPage;
